/**
 * Old dealer-portal addresses, still in bookmarks and emails. Ported 1:1 from
 * frontend/projects/dealer-portal/src/app/app.routes.ts (`legacyRedirects`); `:id` carries over.
 *
 * Checked in the root route's `beforeLoad`, before any route matches, so a legacy address never
 * reaches a page or the not-found handler. The per-type pages land on their type (`?type=`); the
 * old address's own query string is kept.
 */
export const LEGACY_REDIRECTS: readonly (readonly [from: string, to: string])[] = [
  ['/customers', '/customers/tyres'],
  ['/customers/new-cars', '/service-agreement/customers?type=NEW_CAR'],
  ['/customers/used-cars', '/service-agreement/customers?type=USED_CAR'],
  ['/customers/prolongings', '/service-agreement/customers?type=PROLONGING'],
  ['/orders/new-cars', '/service-agreement/orders?type=NEW_CAR'],
  ['/orders/used-cars', '/service-agreement/orders?type=USED_CAR'],
  ['/orders/prolongings', '/service-agreement/orders?type=PROLONGING'],
  ['/user', '/administration/users'],
  ['/user/orgnode/:id', '/administration/organizations/:id'],
  ['/user/orgnode/:id/users', '/administration/users'],
  ['/user/change-password', '/administration/users'],
];

/**
 * Where a legacy `pathname` now lives, or null. Exact matches only, as Angular's
 * `pathMatch: 'full'`. `search` (the old address's query string, with or without `?`) is carried
 * over; where both name a parameter, the target's wins — it is what the old path meant.
 */
export function legacyRedirect(pathname: string, search = ''): string | null {
  const actual = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  for (const [from, to] of LEGACY_REDIRECTS) {
    const pattern = from.split('/').filter(Boolean);
    if (pattern.length !== actual.length) {
      continue;
    }
    const params: Record<string, string> = {};
    const matches = pattern.every((part, i) => {
      if (part.startsWith(':')) {
        params[part.slice(1)] = actual[i]!;
        return true;
      }
      return part === actual[i];
    });
    if (matches) {
      const [path, targetQuery = ''] = to
        .replace(/:(\w+)/g, (_match, name: string) => params[name] ?? '')
        .split('?');
      const query = new URLSearchParams(search.replace(/^\?/, ''));
      new URLSearchParams(targetQuery).forEach((value, key) => query.set(key, value));
      const merged = query.toString();
      return merged ? `${path}?${merged}` : path!;
    }
  }
  return null;
}
